/**
 * Persona Workshop types shared between API and web
 * Guided persona builder with AI suggestions and entity extraction
 */

// ============================================================================
// PERSONA TYPE & MODE
// ============================================================================

export type PersonaType = 'user' | 'buyer'
export type PersonaWorkshopMode = 'quick' | 'deep'
export type PersonaWorkshopStatus = 'active' | 'completed' | 'abandoned'

export type PersonaWorkshopPhaseId =
  | 'identity'
  | 'goals'
  | 'pains'
  | 'behaviors'
  | 'tools'
  | 'voice'
  | 'segment'
  | 'relationships'

// ============================================================================
// SESSION
// ============================================================================

export interface PersonaWorkshopSession {
  id: string
  projectId: string
  accountId: string
  personaType: PersonaType
  mode: PersonaWorkshopMode
  currentPhase: number
  personaData: PersonaData
  extractedEntities: PersonaExtractedEntity[]
  personaEntityUri: string | null
  status: PersonaWorkshopStatus
  createdAt: string
  updatedAt: string
  completedAt: string | null
}

// ============================================================================
// PERSONA DATA (JTBD-structured, replaces flat goals/pains)
// ============================================================================

export type DecisionPower = 'buyer' | 'user' | 'champion' | 'influencer' | 'gatekeeper'

export interface PersonaData {
  // Identity
  name: string
  role: string                           // required
  archetype: string
  context: string                        // replaces demographics
  decisionPower: DecisionPower | ''
  currentTools: string[]                 // replaces tools[]
  constraints: string[]
  photo?: string                         // base64 data URL, 2MB max

  // JTBD-structured (replaces flat goals/pains)
  jtbds: JtbdData[]

  // Legacy fields (kept for backward compat with old sessions)
  demographics?: Record<string, string>
  goals?: string[]
  pains?: PainPoint[]
  tools?: string[]
  quote?: string
  segment?: string | null
  segmentAttributes?: Record<string, string>
  relationships?: PersonaRelationship[]
}

export interface JtbdData {
  id: string
  title: string                          // required
  functional: string                     // required
  emotional?: string
  social?: string
  pains: JtbdPainData[]
  gains: JtbdGainData[]
  customerProblem?: CustomerProblemData
}

export interface JtbdPainData {
  id: string
  description: string
  severity?: 'low' | 'medium' | 'high'
  evidenceCount: number
}

export interface JtbdGainData {
  id: string
  description: string
  importance?: 'nice_to_have' | 'expected' | 'must_have'
  evidenceCount: number
}

export interface CustomerProblemData {
  statement: string
  source: 'manual' | 'ai_synthesised' | 'ai_synthesised_edited'
  contributingPainIds: string[]
  contributingGainIds: string[]
}

export interface PainPoint {
  text: string
  intensity: 'low' | 'medium' | 'high'
}

export interface PersonaRelationship {
  targetPersonaUri: string
  targetPersonaName: string
  relationshipType: 'influences' | 'influenced_by' | 'collaborates' | 'reports_to'
}

export const EMPTY_PERSONA_DATA: PersonaData = {
  name: '',
  role: '',
  archetype: '',
  context: '',
  decisionPower: '',
  currentTools: [],
  constraints: [],
  jtbds: [],
}

/** Migrate old flat PersonaData to JTBD-structured format */
export function migratePersonaData(data: PersonaData): PersonaData {
  if (!data) return { jtbds: [], pains: [], goals: [] } as unknown as PersonaData
  // Already migrated if jtbds exists and has content
  if (data.jtbds && data.jtbds.length > 0) return data

  // Wrap old flat goals/pains into a single "General" JTBD
  const legacyPains = (Array.isArray(data.pains) ? data.pains : []).map((p, i) => ({
    id: `legacy-pain-${i}`,
    description: typeof p === 'string' ? p : p.text,
    severity: (typeof p === 'string' ? 'medium' : p.intensity) as JtbdPainData['severity'],
    evidenceCount: 0,
  }))

  const legacyGoals = (Array.isArray(data.goals) ? data.goals : []).map((g, i) => ({
    id: `legacy-gain-${i}`,
    description: g,
    importance: 'expected' as const,
    evidenceCount: 0,
  }))

  const jtbds: JtbdData[] = (legacyPains.length > 0 || legacyGoals.length > 0)
    ? [{
        id: 'legacy-jtbd-0',
        title: data.goals?.[0] || 'General',
        functional: data.goals?.[0] || '',
        pains: legacyPains,
        gains: legacyGoals,
      }]
    : []

  return {
    ...data,
    context: data.context || Object.values(data.demographics || {}).filter(Boolean).join('. '),
    decisionPower: data.decisionPower || '',
    currentTools: data.currentTools || data.tools || [],
    constraints: data.constraints || [],
    jtbds,
  }
}

// ============================================================================
// AI SUGGESTIONS
// ============================================================================

export interface PersonaSuggestion {
  text: string
  fromProject: boolean
  sourceLabel?: string
}

export interface PersonaSuggestionsResponse {
  suggestions: PersonaSuggestion[]
  archetypes?: string[]
  demographics?: Record<string, string[]>
}

// ============================================================================
// SSE STREAM EVENTS
// ============================================================================

export type PersonaWorkshopStreamEventType =
  | 'suggestions'
  | 'archetypes'
  | 'quote'
  | 'validation'
  | 'done'
  | 'error'

export interface PersonaWorkshopStreamEvent {
  type: PersonaWorkshopStreamEventType
  data: unknown
}

export interface PersonaSuggestionsEvent {
  type: 'suggestions'
  data: { suggestions: PersonaSuggestion[] }
}

export interface PersonaArchetypesEvent {
  type: 'archetypes'
  data: { archetypes: string[]; demographics: Record<string, string[]> }
}

export interface PersonaQuoteEvent {
  type: 'quote'
  data: { quotes: string[] }
}

export interface PersonaValidationEvent {
  type: 'validation'
  data: {
    completeness: number
    gaps: string[]
    suggestions: string[]
  }
}

// ============================================================================
// EXTRACTED ENTITIES
// ============================================================================

export interface PersonaExtractedEntity {
  type: string
  name: string
  domain: 'market' | 'business' | 'solution'
  sourcePhase: PersonaWorkshopPhaseId
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface CreatePersonaWorkshopRequest {
  personaType: PersonaType
  mode: PersonaWorkshopMode
}

export interface UpdatePersonaWorkshopRequest {
  currentPhase?: number
  personaData?: Partial<PersonaData>
}

export interface PersonaWorkshopSuggestRequest {
  phaseId: PersonaWorkshopPhaseId
  apiKey: string
}

export interface CompletePersonaWorkshopRequest {
  apiKey?: string
}

// ============================================================================
// PHASE CONFIGURATION (used by frontend)
// ============================================================================

export const QUICK_PHASES: PersonaWorkshopPhaseId[] = [
  'identity', 'goals', 'pains', 'behaviors',
]

export const DEEP_PHASES: PersonaWorkshopPhaseId[] = [
  'identity', 'goals', 'pains', 'behaviors', 'voice', 'segment', 'relationships',
]
